import { createElement, lazy, type ComponentType } from "react";

/**
 * React.lazy for components that can appear on a prerendered page.
 *
 * A lazy component suspends the first time it renders, even when its chunk
 * is already downloaded. During hydration that leaves its part of the page
 * waiting, and any update from above in the meantime (a provider reacting to
 * the session, the viewport width) makes React drop the server HTML there and
 * show the Suspense fallback instead: the page flashes.
 *
 * So the prerender records which lazy components each page rendered
 * (`collectRenderedLazy`), the list travels in the page, and the browser loads
 * exactly those before hydrating (`preloadLazy`). A loaded component renders
 * at once, without suspending.
 *
 * Every Suspense boundary in the HTML still hydrates one pass after the part
 * of the tree around it (React 18 always defers them), and an update from
 * outside it during that gap throws the server HTML away. The only one a
 * public page should contain is the route boundary in App.tsx, which sits
 * above every provider so nothing outside it can update. Parts inside a page
 * (menu, footer) are imported directly rather than wrapped in a boundary of
 * their own, and anything visitor-specific waits for ClientOnly.
 *
 * `key` names the component in that list. It is a string literal, so it is the
 * same in the browser build and the prerender build. Several lazy wrappers of
 * the same module may share a key; they load together.
 */

type Module<T> = { default: T };

interface Entry {
  preload: () => Promise<unknown>;
  started: boolean;
}

const registry = new Map<string, Entry[]>();
let rendered: Set<string> | null = null;

// React.lazy's own signature: any component, whatever its props.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyComponent<T extends ComponentType<any>>(
  key: string,
  load: () => Promise<Module<T>>,
): ComponentType<React.ComponentProps<T>> {
  let loaded: Module<T> | undefined;
  let loading: Promise<Module<T>> | undefined;

  const entry: Entry = {
    started: false,
    preload: () => {
      entry.started = true;
      loading ??= load().then(
        (m) => (loaded = m),
        (error) => {
          loading = undefined; // a later render may try again
          entry.started = false;
          throw error;
        },
      );
      return loading;
    },
  };
  registry.set(key, [...(registry.get(key) ?? []), entry]);

  // Once the module is loaded the factory hands it over synchronously, and
  // React renders the component without suspending.
  const Lazy = lazy(() =>
    loaded
      ? ({ then: (resolve: (m: Module<T>) => void) => resolve(loaded as Module<T>) } as unknown as Promise<Module<T>>)
      : (entry.preload() as Promise<Module<T>>),
  );

  function Rendered(props: React.ComponentProps<T>) {
    rendered?.add(key);
    return createElement(Lazy as ComponentType<React.ComponentProps<T>>, props);
  }
  Rendered.displayName = `Lazy(${key})`;
  return Rendered;
}

/** Prerender: start recording the lazy components rendered; call the returned function for the list. */
export function collectRenderedLazy(): () => string[] {
  const current = new Set<string>();
  rendered = current;
  return () => {
    if (rendered === current) rendered = null;
    return [...current].sort();
  };
}

/**
 * Browser: load the given lazy components before hydrating. A component can
 * live in a chunk that is itself lazy (the menu inside a page), so it is only
 * known once that chunk has run; loading goes on in rounds until nothing new
 * turns up. Failures are left for the normal lazy path to handle.
 */
export async function preloadLazy(keys: readonly string[], timeoutMs = 10_000): Promise<void> {
  const work = (async () => {
    for (let round = 0; round < 8; round++) {
      const pending = keys.flatMap((key) => (registry.get(key) ?? []).filter((e) => !e.started));
      if (!pending.length) return;
      await Promise.all(pending.map((e) => e.preload().catch(() => undefined)));
    }
  })();
  // A slow network must not keep the page from ever becoming interactive.
  await Promise.race([work, new Promise((resolve) => setTimeout(resolve, timeoutMs))]);
}
